import type {
  CompactStylingDirectives,
  StylingDirectiveNode,
  StylingDirectivesData,
  StylingDirectivesLine,
} from './types'

const EMPTY_LINE: StylingDirectiveNode[] = []
const linesByDocument = new WeakMap<CompactStylingDirectives, Map<number, StylingDirectiveNode[]>>()

export function isCompactStylingDirectives(
  directives: StylingDirectivesData | null | undefined,
): directives is CompactStylingDirectives {
  return !!directives && !Array.isArray(directives) && directives.format === 'compact'
}

export function getLineCount(directives: StylingDirectivesData | null | undefined): number {
  if (!directives) return 0
  if (isCompactStylingDirectives(directives)) return Math.max(0, directives.lineOffsets.length - 1)
  return directives.length
}

/**
 * Calls `callback` for each directive on a line without allocating per-directive
 * objects. Legacy `{s, e, c}` directives are normalized.
 */
export function forEachLineDirective(
  directives: StylingDirectivesData | null | undefined,
  lineIndex: number,
  callback: (start: number, end: number, className: string) => void,
): void {
  if (!directives) return
  if (!isCompactStylingDirectives(directives)) {
    for (const directive of directives[lineIndex] ?? []) {
      if (Array.isArray(directive)) callback(directive[0], directive[1], directive[2])
      else callback(directive.s, directive.e, directive.c)
    }
    return
  }

  const first = directives.lineOffsets[lineIndex]
  const last = directives.lineOffsets[lineIndex + 1]
  if (first === undefined || last === undefined) return
  // Offsets come off the wire, so never walk past the directive columns.
  const limit = Math.min(
    last,
    directives.startsUtf16.length,
    directives.endsUtf16.length,
    directives.classIndexes.length,
  )
  for (let index = first; index < limit; index++) {
    const className = directives.classes[directives.classIndexes[index] ?? -1]
    const start = directives.startsUtf16[index]
    const end = directives.endsUtf16[index]
    if (className !== undefined && start !== undefined && end !== undefined) callback(start, end, className)
  }
}

/**
 * Returns one line's directives. Legacy lines are returned as-is; compact lines
 * are expanded once and cached, so repeated reads return the same array.
 */
export function getLineDirectives(
  directives: StylingDirectivesData | null | undefined,
  lineIndex: number,
): StylingDirectivesLine | undefined {
  if (!directives) return undefined
  if (!isCompactStylingDirectives(directives)) return directives[lineIndex]
  if (!Number.isInteger(lineIndex) || lineIndex < 0 || lineIndex >= getLineCount(directives)) return undefined

  let lines = linesByDocument.get(directives)
  if (!lines) {
    lines = new Map()
    linesByDocument.set(directives, lines)
  }
  let line = lines.get(lineIndex)
  if (!line) {
    const expanded: StylingDirectiveNode[] = []
    forEachLineDirective(directives, lineIndex, (start, end, className) => expanded.push([start, end, className]))
    line = expanded.length ? expanded : EMPTY_LINE
    lines.set(lineIndex, line)
  }
  return line
}
